import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { trackEvent } from "../../lib/analytics";
import { useDashboardStore } from "../../Store/agentZustandStore";

// Maps EVERY SPA route to a stable, human-readable screen_name so GA4
// funnels read cleanly. Unknown routes fall back to an auto-cleaned
// version of the pathname (see toScreenName below), so screen_view always
// carries a proper name — never a raw "/path".
const SCREEN_NAMES = {
  // ── Auth / onboarding ─────────────────────────────────
  "/": "onboarding",
  "/login": "login",
  "/signup": "signup",
  "/details": "personal_details",
  "/fork": "fork_screen",

  // ── Agent build flow ──────────────────────────────────
  "/business-step": "business_setup",
  "/fast-step": "fast_setup",
  "/fast-agent-detail": "agent_dashboard",
  "/agent-detail": "agent_detail",
  "/agent-setup": "agent_setup",
  "/dashboard": "dashboard",
  "/home": "home",

  // ── Plans / payment ───────────────────────────────────
  "/plans": "plans",
  "/plan": "plan",
  "/recommended-plan": "recommended_plan",
  "/build-own-plan": "build_own_plan",
  "/build-plan": "build_plan",
  "/own-plan": "own_plan",
  "/own-custom-plan": "own_custom_plan",
  "/message-plan": "message_plan",
  "/checkout": "checkout",
  "/payment-success": "payment_success",
  "/cancel-payment": "payment_cancelled",
  "/thankyou2": "thankyou_v2",
  "/website-trial-success": "website_trial_success",

  // ── Post-purchase / go-live ───────────────────────────
  "/steps": "post_purchase_steps",
  "/post-purchase-setup": "post_purchase_setup",
  "/go-live-setup": "go_live_setup",
  "/go-live-celebration": "go_live_complete",
  "/setup-ready": "setup_ready",
  "/select-carrier": "select_carrier",
  "/forward-instructions": "forward_instructions",

  // ── Numbers ───────────────────────────────────────────
  "/number": "number",
  "/get-number": "get_number",
  "/assign-number": "assign_number",
  "/number-assign": "number_assign",
  "/immediate-owner-ship": "immediate_ownership",

  // ── Calls ─────────────────────────────────────────────
  "/calinfo": "call_info",
  "/call_divert": "call_divert",
  "/call-recording": "call_recording",
  "/call-setting": "call_settings",
  "/call-transfer": "call_transfer",
  "/totalcall-list": "call_list",
  "/screen-your-calls": "screen_your_calls",

  // ── Contacts ──────────────────────────────────────────
  "/contacts": "contacts",
  "/contact-sync": "contact_sync",

  // ── Business info ─────────────────────────────────────
  "/about-business": "about_business",
  "/about-business-next": "about_business_next",
  "/business-details": "business_details",
  "/business-locations": "business_locations",
  "/business-services": "business_services",
  "/Business_hours": "business_hours",
  "/your-business-Listing": "your_business_listing",

  // ── Calendar / integrations ───────────────────────────
  "/calendar": "calendar",
  "/calendar-access": "calendar_access",
  "/connect-calender": "connect_calendar",
  "/connect-calender2": "connect_calendar_v2",
  "/integrations": "integrations",
  "/integrate-agent": "integrate_agent",
  "/mcp-integration": "mcp_integration",
  "/ai-assistant": "ai_assistant",
  "/rex-agent": "rex_agent",

  // ── Edit screens ──────────────────────────────────────
  "/edit-agent": "edit_agent",
  "/edit-profile": "edit_profile",
  "/edit-business-detail": "edit_business_detail",
  "/editBusinessDetails": "edit_business_details",
  "/edit-business-type": "edit_business_type",
  "/edit-gender": "edit_gender",
  "/edit-language": "edit_language",
  "/edit-name-avtar": "edit_name_avatar",
  "/edit-public": "edit_public_listing",
  "/edit-public-listing": "edit_public_listing",
  "/edit-services-offered": "edit_services_offered",

  // ── Misc ──────────────────────────────────────────────
  "/usage-history": "usage_history",
  "/notifications": "notifications",
  "/documentation": "documentation",
  "/widget-guide": "widget_guide",
  "/create-ticket": "create_ticket",
  "/raise-tickets": "raise_tickets",
  "/delete-account": "delete_account",
  "/public-agent-availability": "public_agent_availability",
  "/add-file": "add_file",
  "/the-comparison": "the_comparison",
  "/the-solution": "the_solution",
  "/test-other": "test_other",
};

// Routes with a dynamic segment (:id, :token ...) — matched by pattern so
// e.g. /thankyou/abc123 and /thankyou/xyz789 both report "payment_thankyou".
const DYNAMIC_SCREENS = [
  [/^\/thankyou\/[^/]+$/i, "payment_thankyou"],
  [/^\/call-details\/[^/]+$/i, "call_details"],
  [/^\/business_hours\/[^/]+$/i, "business_hours"],
  [/^\/agents\/vip-numbers\/[^/]+$/i, "vip_numbers"],
  [/^\/public-call\/[^/]+$/i, "public_call"],
  [/^\/setup-ready\/[^/]+$/i, "setup_ready_feature"],
  [/^\/integrations\/[^/]+\/success$/i, "integration_success"],
  [/^\/oauth\/[^/]+\/consent$/i, "oauth_consent"],
];

// Resolve any pathname to a clean screen_name:
// 1. exact match in SCREEN_NAMES
// 2. dynamic-route pattern match
// 3. fallback: auto-clean the path ("/some-new-page" → "some_new_page") so
//    even brand-new routes never report a raw "/path" as the screen name.
const toScreenName = (path) => {
  if (SCREEN_NAMES[path]) return SCREEN_NAMES[path];
  const dyn = DYNAMIC_SCREENS.find(([re]) => re.test(path));
  if (dyn) return dyn[1];
  return (
    path
      .replace(/^\/+|\/+$/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "") || "onboarding"
  );
};

export default function RouteTracker() {
  const location = useLocation();
  // /recommended-plan is two different screens. With no mode it is the
  // onboarding recommended-plan step; with ?mode=plans it is the plans /
  // upgrade screen that every Upgrade button in the app opens
  // (FastAgentDetails) and lifecycle emails link to. The /plans route itself
  // is not linked from anywhere, so visit_plans_view never fired, and the two
  // screens were indistinguishable under visit_recommended_plan_view.
  // RecommendedPlan keeps ?mode in the URL while it is open, so this is stable
  // for the whole visit.
  const planPath = location.pathname.toLowerCase();
  const isPlansMode =
    (planPath === "/recommended-plan" || planPath === "/recommended-plan/") &&
    new URLSearchParams(location.search).get("mode") === "plans";

  useEffect(() => {
    try {
      window.scrollTo(0, 0);
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
      const scrollContainers = document.querySelectorAll('.custom-refresh-wrapper, [class*="wrapper"], [class*="container"], [class*="page"]');
      scrollContainers.forEach((el) => {
        if (el && typeof el.scrollTop === 'number' && el.scrollTop > 0) {
          el.scrollTop = 0;
        }
      });
    } catch (e) {}

    const path = location.pathname;
    // Per-page event names instead of one generic screen_view — each screen
    // shows up in the GA4 Events list with its own count, no param drilling:
    //   /personal-details → visit_personal_details_view
    //   /checkout         → visit_checkout_view
    const screenName = isPlansMode ? "plans" : toScreenName(path);
    trackEvent(`visit_${screenName}_view`, { screen_name: screenName, path });
    // Keyed on isPlansMode, not location.search, so unrelated query-string
    // changes on the same page do not re-fire the visit.
  }, [location.pathname, isPlansMode]);

  // Native app: Google Calendar connected. The backend deep-links the app via
  // com.rexptin://calendar and the native shell posts GOOGLE_CALENDAR_CONNECTED
  // into this WebView, but no screen ever listened for it, so native calendar
  // connects were never tracked. RouteTracker is always mounted, which makes
  // it the one place guaranteed to receive the message whichever screen is
  // showing. iOS delivers on window, Android on document: listen on both.
  useEffect(() => {
    if (typeof window === "undefined" || !window.ReactNativeWebView) return undefined;
    const onNativeMessage = (event) => {
      let msg;
      try {
        msg = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch {
        return; // not JSON, not ours
      }
      if (msg?.type === "GOOGLE_CALENDAR_CONNECTED") {
        trackEvent("calendar_sync_completed", { source: "native_app" });
        // In the app the WebView is not reloaded after Google sign-in, so the
        // Dashboard's cached agent list still says "not connected" — refetch.
        useDashboardStore.getState().setHasFetched(false);
      }
    };
    window.addEventListener("message", onNativeMessage);
    document.addEventListener("message", onNativeMessage);
    return () => {
      window.removeEventListener("message", onNativeMessage);
      document.removeEventListener("message", onNativeMessage);
    };
  }, []);

  return null;
}
